/**
 * measurements.js — Registro de mediciones de seguimiento (Wellness Hub)
 *
 * Cada medición se guarda como documento inmutable en:
 *   users/{uid}/measurements/{autoId}
 *
 * Además, los flat fields relevantes (peso, talla, PA, glicemia, oximetría,
 * heartRate, etc.) se actualizan en el profile para que el Dashboard siga
 * reflejando el último valor registrado.
 *
 * Sincroniza a GCP vía POST /api/patients/metrics (LOINC) de forma
 * fire-and-forget — un fallo en GCP nunca bloquea el guardado local.
 */
import { db } from "@/firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit as fbLimit,
  serverTimestamp,
} from "firebase/firestore";
import { OBSERVATION_DEFS } from "@/data/loinc";
import { CORE_API_URL, CORE_API_KEY, getCoreServicesConfig } from "./coreServicesClient";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;

/**
 * Helper interno para esperar en reintentos con backoff exponencial.
 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Envía el payload de métricas LOINC al proxy de GCP (o fallback a Core Services)
 * con soporte de reintentos ante fallos transitorios de red o 5xx.
 */
async function postMetricsWithRetry(payload, fallbackMetricsUrl, maxRetries = 2) {
  let lastError = null;
  let lastStatus = 0;
  const coreConfig = typeof getCoreServicesConfig === "function" ? getCoreServicesConfig() : null;
  const targetUrl = coreConfig?.apiUrl || coreConfig?.url;
  const reqHeaders = {
    "Content-Type": "application/json",
    ...(targetUrl ? { "x-core-target-url": targetUrl } : {}),
    ...(coreConfig?.apiKey ? { "x-core-api-key": coreConfig.apiKey } : {})
  };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      await sleep(400 * Math.pow(2, attempt - 1));
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/core-services/proxy/patients/metrics`, {
        method: "POST",
        headers: reqHeaders,
        body: JSON.stringify(payload),
      });
      lastStatus = res.status;
      if (res.ok) {
        return { ok: true, status: res.status };
      }
      // Si el proxy local falla o devuelve error, intentar fallback directo a Core Services
      const fallbackRes = await fetch(fallbackMetricsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": CORE_API_KEY,
        },
        body: JSON.stringify(payload),
      });
      lastStatus = fallbackRes.status;
      if (fallbackRes.ok) {
        return { ok: true, status: fallbackRes.status };
      }
      // Si es un error 4xx distinto de 404 (404 puede ocurrir si Patient aún se está indexando en FHIR), no reintentar
      if (fallbackRes.status >= 400 && fallbackRes.status < 500 && fallbackRes.status !== 404) {
        return { ok: false, status: fallbackRes.status, error: `HTTP ${fallbackRes.status}` };
      }
      lastError = `HTTP ${fallbackRes.status}`;
    } catch (err) {
      lastError = err.message;
      try {
        const fallbackRes = await fetch(fallbackMetricsUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": CORE_API_KEY,
          },
          body: JSON.stringify(payload),
        });
        lastStatus = fallbackRes.status;
        if (fallbackRes.ok) {
          return { ok: true, status: fallbackRes.status };
        }
        lastError = `HTTP ${fallbackRes.status}`;
      } catch (directErr) {
        lastError = directErr.message;
      }
    }
  }

  return { ok: false, status: lastStatus, error: lastError || "Sync failed" };
}

/**
 * Sincroniza métricas a GCP FHIR Store (Fuente de la Verdad) implementando el patrón Outbox.
 * Actualiza el estado `syncStatus` ("SYNCED" | "PENDING_FHIR") en Firestore para garantizar
 * entrega eventual si ocurre un corte de red.
 *
 * @param {string} email
 * @param {Object} fields
 * @param {{ uid?: string, measurementId?: string, maxRetries?: number }} [options]
 * @returns {Promise<{ ok: boolean, status?: number, skipped?: boolean, error?: string }>}
 */
export async function syncMetricsToGCP(email, fields, options = {}) {
  const { uid, measurementId, maxRetries = 2 } = options || {};
  if (!email || !fields) return { ok: false, error: "email y fields requeridos" };

  const metricMap = new Map();

  // Si fields es un perfil FHIR con array de observations, extraer primero desde ahí
  if (Array.isArray(fields.observations)) {
    fields.observations.forEach((obs) => {
      if (obs && obs.code && obs.value != null && obs.value !== "" && (!obs.system || obs.system === "http://loinc.org")) {
        if (["72166-2", "74013-4", "93832-4"].includes(obs.code)) return;
        const numVal = typeof obs.value === "string" ? parseFloat(obs.value) : obs.value;
        if (typeof numVal === "number" && !Number.isNaN(numVal)) {
          metricMap.set(obs.code, {
            code: obs.code,
            display: obs.display || obs.code,
            value: numVal,
            unit: obs.unit || undefined,
          });
        }
      }
    });
  }

  Object.entries(fields)
    .filter(([k, v]) =>
      v !== null && v !== undefined && v !== "" &&
      OBSERVATION_DEFS[k] &&
      OBSERVATION_DEFS[k].system === "http://loinc.org"  // solo LOINC estándar — GCP FHIR rechaza códigos custom
    )
    .forEach(([k, v]) => {
      const def = OBSERVATION_DEFS[k];
      metricMap.set(def.code, {
        code: def.code,
        display: def.display,
        value: typeof v === "string" ? parseFloat(v) || v : v,
        unit: def.unit || undefined,
      });
    });

  const metrics = Array.from(metricMap.values());

  const nowIso = new Date().toISOString();

  if (metrics.length === 0) {
    if (uid && measurementId) {
      try {
        const { doc, setDoc } = await import("firebase/firestore");
        await setDoc(
          doc(db, "users", uid, "measurements", measurementId),
          { syncStatus: "SYNCED", fhirSyncedAt: nowIso },
          { merge: true }
        );
      } catch {}
    }
    return { ok: true, skipped: true };
  }

  const fallbackMetricsUrl = `${CORE_API_URL.replace(/\/+$/, "")}/patients/metrics`;
  const result = await postMetricsWithRetry({ email, metrics }, fallbackMetricsUrl, maxRetries);

  if (uid) {
    try {
      const { doc, setDoc } = await import("firebase/firestore");
      if (measurementId) {
        try {
          await setDoc(
            doc(db, "users", uid, "measurements", measurementId),
            result.ok
              ? { syncStatus: "SYNCED", fhirSyncedAt: nowIso, lastSyncError: null }
              : { syncStatus: "PENDING_FHIR", lastSyncAttemptAt: nowIso, lastSyncError: result.error || "Error FHIR" },
            { merge: true }
          );
        } catch (subErr) {
          console.warn("[GCP metrics] No se pudo actualizar medición Outbox en Firestore:", subErr.message);
        }
      }
      await setDoc(
        doc(db, "users", uid),
        result.ok
          ? {
              fhirSyncStatus: "SYNCED",
              pendingFhirMetrics: false,
              lastFhirSyncAt: nowIso,
              "profile.fhirSyncStatus": "SYNCED",
              "profile.pendingFhirMetrics": false,
              "profile.lastFhirSyncAt": nowIso,
            }
          : {
              fhirSyncStatus: "PENDING_FHIR",
              pendingFhirMetrics: true,
              lastFhirSyncError: result.error || "Error FHIR",
              "profile.fhirSyncStatus": "PENDING_FHIR",
              "profile.pendingFhirMetrics": true,
            },
        { merge: true }
      );
    } catch (fsErr) {
      console.warn("[GCP metrics] No se pudo actualizar estado Outbox en Firestore:", fsErr.message);
    }
  }

  if (!result.ok) {
    console.warn("[GCP metrics] Sync pendiente en Outbox (se reintentará):", result.error);
  }

  return result;
}

/** Campos numéricos aceptados en una medición manual o por cámara. */
export const MEASUREMENT_FIELDS = [
  "peso",
  "talla",
  "cintura",
  "paSistolica",
  "paDiastolica",
  "glucemia",
  "oximetria",
  "heartRate",
  "heartRateVariability",
  "respirationRate",
  "stressIndex",
  "cardiovascularAge",
  "cardiovascularBMI",
  "cvdRisk",
  "wellnessScore",
  "ansIndexPNS",
  "ansIndexSNS",
  "hba1c",
  "hemoglobin",
  "cholesterol",
  "shockIndex",
  "prq",
  "hrvRMSSD",
  "hrvSDNN",
];

/**
 * Construye el objeto `fields` descartando valores vacíos/inválidos.
 * Siempre se almacena como número cuando es parseable.
 */
function cleanFields(raw) {
  const out = {};
  for (const k of MEASUREMENT_FIELDS) {
    const v = raw?.[k];
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
    if (!Number.isNaN(n)) out[k] = n;
  }
  return out;
}

/**
 * Guarda una nueva medición inmutable en la subcolección de historial.
 *
 * @param {string} uid - Firebase UID del usuario.
 * @param {{ method: 'camera'|'manual', fields: Object, note?: string }} data
 * @returns {Promise<{id: string, fields: Object, timestamp: string, method: string}>}
 *
 * NOTA: Esta función NO toca el `profile` del usuario. La actualización de los
 * flat fields (peso, paSistolica, etc.) para que el Dashboard refleje el último
 * valor debe hacerse desde el componente vía el UserProfileContext (setProfile).
 * Así mantenemos una sola fuente de verdad FHIR y evitamos escrituras duplicadas.
 */
export async function saveMeasurement(uid, data) {
  if (!uid) throw new Error("uid requerido");
  if (!data?.method) throw new Error("method requerido (camera|manual)");

  const fields = cleanFields(data.fields || {});
  if (Object.keys(fields).length === 0) {
    throw new Error("Debes registrar al menos un valor");
  }

  const nowIso = new Date().toISOString();
  const payload = {
    method: data.method,
    source: "wellness-hub",
    fields,
    note: data.note || null,
    createdAt: nowIso,
    timestamp: serverTimestamp(),
    syncStatus: "PENDING_FHIR",
    fhirSyncedAt: null,
  };

  // Append inmutable a la subcolección de historial (Outbox temporal hacia FHIR)
  const colRef = collection(db, "users", uid, "measurements");
  const ref = await addDoc(colRef, payload);

  return {
    id: ref.id,
    fields,
    timestamp: nowIso,
    method: data.method,
    syncStatus: "PENDING_FHIR",
  };
}

/**
 * Lista las últimas N mediciones del usuario, ordenadas por fecha descendente.
 * @param {string} uid
 * @param {number} [limit=20]
 */
export async function listMeasurements(uid, limit = 20) {
  if (!uid) return [];
  const colRef = collection(db, "users", uid, "measurements");
  const q = query(colRef, orderBy("createdAt", "desc"), fbLimit(limit));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      method: data.method,
      source: data.source,
      fields: data.fields || {},
      note: data.note,
      createdAt: data.createdAt,
      // Mediciones históricas previas al patrón Outbox se asumen ya sincronizadas
      syncStatus: data.syncStatus || "SYNCED",
      fhirSyncedAt: data.fhirSyncedAt || null,
    };
  });
}

/**
 * Drena (Flush) la cola Outbox de mediciones en Firestore que quedaron con
 * `syncStatus === "PENDING_FHIR"` (por ejemplo, tras cierre de pestaña o caída de red)
 * enviándolas en orden cronológico al FHIR Store de GCP.
 *
 * @param {string} uid - Firebase UID del usuario.
 * @param {string} email - Correo del usuario para identificar el recurso Patient en FHIR.
 * @returns {Promise<{ flushedCount: number, pendingRemaining: number, allSucceeded: boolean }>}
 */
export async function flushPendingMeasurementsToGCP(uid, email) {
  if (!uid || !email) {
    return { flushedCount: 0, pendingRemaining: 0, allSucceeded: true };
  }
  try {
    const recent = await listMeasurements(uid, 50);
    const pending = recent
      .filter((m) => m.syncStatus === "PENDING_FHIR" || m.syncStatus === "FAILED")
      .reverse(); // Orden cronológico: más antigua a más reciente

    if (pending.length === 0) {
      return { flushedCount: 0, pendingRemaining: 0, allSucceeded: true };
    }

    let flushedCount = 0;
    let failedCount = 0;

    for (const item of pending) {
      const res = await syncMetricsToGCP(email, item.fields, {
        uid,
        measurementId: item.id,
        maxRetries: 2,
      });
      if (res.ok) {
        flushedCount++;
      } else {
        failedCount++;
      }
    }

    if (failedCount === 0 && flushedCount > 0) {
      try {
        const { doc, setDoc } = await import("firebase/firestore");
        const nowIso = new Date().toISOString();
        await setDoc(
          doc(db, "users", uid),
          {
            fhirSyncStatus: "SYNCED",
            pendingFhirMetrics: false,
            lastFhirSyncAt: nowIso,
            "profile.fhirSyncStatus": "SYNCED",
            "profile.pendingFhirMetrics": false,
            "profile.lastFhirSyncAt": nowIso,
          },
          { merge: true }
        );
      } catch {}
    }

    return {
      flushedCount,
      pendingRemaining: failedCount,
      allSucceeded: failedCount === 0,
    };
  } catch (err) {
    console.warn("[Outbox Flush] Error drenando mediciones pendientes:", err.message);
    return { flushedCount: 0, pendingRemaining: 1, allSucceeded: false };
  }
}

/** Helpers de UI para etiquetas y unidades. */
export const FIELD_META = {
  peso:                  { label: "Peso corporal",             unit: "kg",    min: 20,  max: 300, step: 0.1, placeholder: "72.5" },
  talla:                 { label: "Estatura",                  unit: "cm",    min: 50,  max: 250, step: 1,   placeholder: "170" },
  cintura:               { label: "Cintura abdominal",         unit: "cm",    min: 40,  max: 200, step: 1,   placeholder: "85" },
  paSistolica:           { label: "Sistólica (máxima)",        unit: "mmHg",  min: 60,  max: 250, step: 1,   placeholder: "120" },
  paDiastolica:          { label: "Diastólica (mínima)",       unit: "mmHg",  min: 30,  max: 150, step: 1,   placeholder: "80" },
  glucemia:              { label: "Glucemia en ayunas",        unit: "mg/dl", min: 30,  max: 600, step: 1,   placeholder: "95" },
  oximetria:             { label: "Saturación de oxígeno",     unit: "%",     min: 50,  max: 100, step: 1,   placeholder: "98" },
  heartRate:             { label: "Frecuencia cardíaca",       unit: "lpm",   min: 30,  max: 220, step: 1,   placeholder: "72" },
  heartRateVariability:  { label: "Variabilidad cardíaca (HRV)", unit: "ms",  min: 0,   max: 500, step: 1,   placeholder: "45" },
  respirationRate:       { label: "Frecuencia respiratoria",   unit: "rpm",   min: 5,   max: 60,  step: 1,   placeholder: "16" },
  stressIndex:           { label: "Índice de estrés (Baevsky)",unit: "",      min: 0,   max: 1000, step: 1,   placeholder: "150" },
  cardiovascularAge:     { label: "Edad cardiovascular",       unit: "años",  min: 10,  max: 120, step: 1,   placeholder: "35" },
  cardiovascularBMI:     { label: "IMC cardiovascular",        unit: "",      min: 10,  max: 60,  step: 0.1, placeholder: "24.5" },
  cvdRisk:               { label: "Riesgo cardiovascular",     unit: "%",     min: 0,   max: 100, step: 0.1, placeholder: "3.2" },
  wellnessScore:         { label: "Puntaje de bienestar",      unit: "/10",   min: 0,   max: 10,  step: 0.1, placeholder: "7.5" },
  ansIndexPNS:           { label: "Sistema parasimpático",     unit: "",      min: -5,  max: 5,   step: 0.1, placeholder: "1.2" },
  ansIndexSNS:           { label: "Sistema simpático",         unit: "",      min: -5,  max: 5,   step: 0.1, placeholder: "0.8" },
};
